// Shared math, formatting, and color logic for the Truth Chart tabs.
// Ported from Truth Chart 2.0's app.js — the ±2% margin band and the
// green/yellow/red semantics must stay identical to the original report
// card so numbers match what reps were shown before.

export const fmt = {
  dollars: (v) => (v == null ? "—" : "$" + Math.round(v).toLocaleString()),
  pct: (v) => (v == null ? "—" : v.toFixed(2) + "%"),
};

export const margin = (sales, cost) => {
  if (!sales || sales === 0) return null;
  return ((sales - cost) / sales) * 100;
};

// Which heat color a rep margin gets vs. the company margin (±2% band).
export const marginBand = (repMargin, companyMargin, hasSales) => {
  if (!hasSales || repMargin == null) return "noSales";
  const diff = repMargin - companyMargin;
  if (diff > 2) return "green";
  if (diff < -2) return "red";
  return "yellow";
};

// The stored proc pre-computes a Color flag per PG2 row: 1=below, 2=within,
// 3=above target.
export const barBand = (color) => {
  if (color === 1) return "red";
  if (color === 2) return "yellow";
  if (color === 3) return "green";
  return "none";
};

// Heat-cell styling. Light mode keeps the original report's exact Excel-ish
// fills; dark mode swaps to translucent tints so the table stays readable
// on the dark canvas.
export const CELL_CLASSES = {
  green:
    "bg-[#92d050] font-bold text-[#1a3d00] dark:bg-green-500/30 dark:text-green-200",
  yellow: "bg-[#ffff80] text-[#554400] dark:bg-yellow-500/25 dark:text-yellow-100",
  red: "bg-[#ff9999] font-bold text-[#700000] dark:bg-red-500/30 dark:text-red-200",
  noSales:
    "bg-[#f5e9e9] italic text-neutral-400 dark:bg-slate-700/40 dark:text-slate-500",
};

// Same bands rendered on the dark TOTAL row, where translucent tints would
// vanish — solid fills in both themes.
export const TOTAL_CELL_CLASSES = {
  green: "bg-[#5a9a20] font-bold text-white",
  yellow: "bg-[#aaaa00] font-bold text-white",
  red: "bg-[#cc3333] font-bold text-white",
  noSales: "italic text-slate-400",
};

export const BAR_CLASSES = {
  green: "bg-[#92d050] text-[#1a3d00]",
  yellow: "bg-[#ffff80] text-[#554400]",
  red: "bg-[#ff9999] text-[#700000]",
  none: "bg-[#f0e4e4] font-normal italic text-neutral-400 dark:bg-slate-700/50 dark:text-slate-500",
};

// Rep / period summary strip above each report table.
export const INFO_STRIP =
  "app-surface mb-3 flex flex-wrap gap-x-5 gap-y-1 rounded-xl border px-3 py-2 text-xs tabular-nums";
export const INFO_LABEL = "app-muted font-semibold";

// ▲/▼ over-under indicator vs. a target margin.
export const OverUnder = ({ repMargin, targetMargin, hasSales }) => {
  if (!hasSales || repMargin == null) {
    return (
      <span className="text-[11px] italic text-neutral-400 dark:text-slate-500">
        No Sales
      </span>
    );
  }
  const diff = repMargin - targetMargin;
  const up = diff > 0;
  return (
    <span
      className={`text-[11px] font-semibold ${
        up
          ? "text-[#2a7a00] dark:text-green-400"
          : "text-[#c00000] dark:text-red-400"
      }`}
    >
      {up ? "▲" : "▼"} {Math.abs(diff).toFixed(2)}%
    </span>
  );
};

// Strip the "NN - " prefix and abbreviate a PG1 name for column headers.
export const shortName = (name) => {
  if (!name) return "";
  const m = name.match(/^\d+\s*-\s*(.+)/);
  const raw = m ? m[1] : name;
  const words = raw.split(/[\s,/]+/);
  let out = words[0];
  if (out.length <= 6 && words[1]) out += " " + words[1];
  return out.length > 14 ? out.substring(0, 13) + "…" : out;
};

// Pivot the proc's flat rows (customer × PG2) into the shapes the tabs
// need: the PG1 column list, per-customer rollups with nested PG1/PG2
// detail, and the company-wide margin.
export const transformRows = (rows) => {
  const pg1Map = {};
  const custMap = {};
  const overallCo = rows.length ? rows[0]["Company Margin"] || 0 : 0;

  rows.forEach((row) => {
    const p1id = row["PG1ID"];
    if (!pg1Map[p1id]) {
      pg1Map[p1id] = {
        id: p1id,
        name: row["PG1"],
        companyMargin: row["PG1 Company"] || 0,
      };
    }

    const cid = row["CustomerID"];
    if (!custMap[cid]) {
      custMap[cid] = {
        id: cid,
        name: row["Customer"],
        type: row["Name"] || "",
        totalSales: 0,
        totalCost: 0,
        pg1s: {},
      };
    }
    const cust = custMap[cid];
    cust.totalSales += row["TotalSales"] || 0;
    cust.totalCost += row["TotalCost"] || 0;

    if (!cust.pg1s[p1id]) {
      cust.pg1s[p1id] = {
        id: p1id,
        name: row["PG1"],
        sales: 0,
        cost: 0,
        companyMargin: row["PG1 Company"] || 0,
        pg2s: [],
      };
    }
    const pg1 = cust.pg1s[p1id];
    pg1.sales += row["TotalSales"] || 0;
    pg1.cost += row["TotalCost"] || 0;
    pg1.pg2s.push(row);
  });

  return {
    pg1List: Object.values(pg1Map).sort((a, b) => a.id - b.id),
    custList: Object.values(custMap).sort((a, b) => b.totalSales - a.totalSales),
    overallCo,
  };
};
